import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/cliente';
import { Cargando, Error } from '../componentes/comunes';
import { folioOT } from '../folio';

/**
 * El tablero de Dirección.
 *
 * Cuatro bloques en el orden en que sirven: lo que espera su firma, el
 * dinero del mes, la operación y el margen. El primero va arriba porque
 * es lo único que se resuelve desde aquí; el resto se mira.
 *
 * Sin gráficas. Con dos meses de historia una curva dibuja una recta
 * entre dos puntos y la gente le lee una dirección que los datos no
 * sostienen. Cuando haya un año se agregan, y hasta entonces los números
 * dicen más.
 */

interface Bloque {
  etiqueta: string;
  monto: number;
  anterior: number;
  variacion: number | null;
}

interface Tablero {
  generadoEn: string;
  firmas: {
    compras: {
      pendientes: number; monto: number; diasMasViejo: number | null;
      urgencia: 'ninguna' | 'atender' | 'tarde';
    };
    pagos: {
      pendientes: number; monto: number; aProveedores: number; aTecnicos: number;
      diasMasViejo: number | null; urgencia: 'ninguna' | 'atender' | 'tarde';
    };
  };
  dinero: {
    comprado: Bloque;
    pagadoProveedores: Bloque;
    pagadoTecnicos: Bloque;
    porFacturar: { ordenes: number; monto: number; sinPrecio: number };
  };
  operacion: {
    abiertas: number; vencidas: number; esperandoRefacciones: number;
    sinAsignar: number; cerradasDelMes: number;
  };
  margen: {
    ordenes: number; venta: number; costo: number; utilidad: number;
    porcentaje: number | null; variacionUtilidad: number | null;
    negativas: {
      folio: string; sucursal: string | null;
      venta: number; costo: number; utilidad: number; porcentaje: number | null;
    }[];
  };
}

function moneda(valor: number): string {
  return valor.toLocaleString('es-MX', {
    style: 'currency', currency: 'MXN', maximumFractionDigits: 0,
  });
}

const ROJO = '#b02a37';
const VERDE = '#166534';

/** El cambio contra el mes pasado, si es que significa algo. */
function Variacion({ valor, alRevés }: { valor: number | null; alRevés?: boolean }) {
  if (valor === null) {
    return <span className="ayuda">sin comparación</span>;
  }

  // Gastar más no es bueno y facturar más no es malo: quién manda el
  // color depende de la cifra, no del signo.
  const bueno = alRevés ? valor <= 0 : valor >= 0;

  return (
    <span className="ayuda" style={{ color: bueno ? VERDE : ROJO }}>
      {valor > 0 ? '+' : ''}{valor}% vs. mes pasado
    </span>
  );
}

function Cifra({
  titulo, valor, detalle, alerta, onClick,
}: {
  titulo: string;
  valor: string;
  detalle?: React.ReactNode;
  alerta?: boolean;
  onClick?: () => void;
}) {
  return (
    <div
      className="tarjeta"
      style={{ cursor: onClick ? 'pointer' : 'default' }}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
    >
      <div className="conteo">{titulo}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: alerta ? ROJO : undefined }}>
        {valor}
      </div>
      {detalle && <div style={{ marginTop: 2 }}>{detalle}</div>}
    </div>
  );
}

export default function Tablero({
  alVerCompras, alVerPagos,
}: {
  /** Las firmas se resuelven en las otras pestañas de esta misma sección. */
  alVerCompras?: () => void;
  alVerPagos?: () => void;
} = {}) {
  const navegar = useNavigate();
  const [datos, setDatos] = useState<Tablero | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    api.get<Tablero>('/direccion/tablero').then(setDatos).catch(setError);
  }, []);

  if (error) return <Error error={error} />;
  if (!datos) return <Cargando />;

  const { firmas, dinero, operacion, margen } = datos;
  const esperan = firmas.compras.pendientes + firmas.pagos.pendientes;

  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Dirección</h1>
          <p className="sub">
            Cómo va la empresa este mes, y qué está esperando tu firma.
          </p>
        </div>
      </div>

      {/* ── 1. Lo único accionable de la pantalla ─────────────────── */}
      <h2>Espera tu firma</h2>

      {esperan === 0 ? (
        <div className="panel">
          <div className="vacio">Nada pendiente de autorizar.</div>
        </div>
      ) : (
        <div className="rejilla-catalogos">
          <Cifra
            titulo="Órdenes de compra"
            valor={String(firmas.compras.pendientes)}
            alerta={firmas.compras.urgencia === 'tarde'}
            onClick={alVerCompras}
            detalle={
              <>
                <div>{moneda(firmas.compras.monto)} comprometidos</div>
                {firmas.compras.diasMasViejo !== null && firmas.compras.diasMasViejo > 0 && (
                  <div className="ayuda">
                    La más vieja lleva {firmas.compras.diasMasViejo} días
                  </div>
                )}
              </>
            }
          />
          <Cifra
            titulo="Pagos"
            valor={String(firmas.pagos.pendientes)}
            alerta={firmas.pagos.urgencia === 'tarde'}
            onClick={alVerPagos}
            detalle={
              <>
                <div>{moneda(firmas.pagos.monto)} por autorizar</div>
                <div className="ayuda">
                  {firmas.pagos.aProveedores} a proveedores · {firmas.pagos.aTecnicos} a técnicos
                </div>
              </>
            }
          />
        </div>
      )}

      {/* ── 2. El dinero ──────────────────────────────────────────── */}
      <h2>El dinero de este mes</h2>
      <div className="rejilla-catalogos">
        {[dinero.comprado, dinero.pagadoProveedores, dinero.pagadoTecnicos].map((b) => (
          <Cifra
            key={b.etiqueta}
            titulo={b.etiqueta}
            valor={moneda(b.monto)}
            detalle={<Variacion valor={b.variacion} alRevés />}
          />
        ))}
        <Cifra
          titulo="Cerrado por facturar"
          valor={moneda(dinero.porFacturar.monto)}
          onClick={() => navegar('/facturacion')}
          detalle={
            <>
              <div className="ayuda">{dinero.porFacturar.ordenes} órdenes</div>
              {dinero.porFacturar.sinPrecio > 0 && (
                <div className="ayuda" style={{ color: ROJO }}>
                  {dinero.porFacturar.sinPrecio} sin precio fijado
                </div>
              )}
            </>
          }
        />
      </div>

      {/* ── 3. La operación ───────────────────────────────────────── */}
      <h2>La operación</h2>
      <div className="rejilla-catalogos">
        <Cifra
          titulo="Órdenes abiertas"
          valor={String(operacion.abiertas)}
          onClick={() => navegar('/ordenes')}
        />
        <Cifra
          titulo="Vencidas"
          valor={String(operacion.vencidas)}
          alerta={operacion.vencidas > 0}
          onClick={() => navegar('/ordenes')}
          detalle={<span className="ayuda">Pasaron su fecha límite</span>}
        />
        <Cifra
          titulo="Esperando refacciones"
          valor={String(operacion.esperandoRefacciones)}
          detalle={<span className="ayuda">Paradas por falta de pieza</span>}
        />
        <Cifra
          titulo="Sin técnico"
          valor={String(operacion.sinAsignar)}
          alerta={operacion.sinAsignar > 0}
        />
        <Cifra
          titulo="Cerradas este mes"
          valor={String(operacion.cerradasDelMes)}
        />
      </div>

      {/* ── 4. El margen ──────────────────────────────────────────── */}
      <h2>Margen de lo cerrado este mes</h2>

      {margen.ordenes === 0 ? (
        <div className="panel">
          <div className="vacio">Todavía no se cierra ninguna orden este mes.</div>
        </div>
      ) : (
        <>
          <div className="rejilla-catalogos">
            <Cifra titulo="Venta" valor={moneda(margen.venta)} />
            <Cifra titulo="Costo" valor={moneda(margen.costo)} />
            <Cifra
              titulo="Utilidad"
              valor={moneda(margen.utilidad)}
              alerta={margen.utilidad < 0}
              detalle={<Variacion valor={margen.variacionUtilidad} />}
            />
            <Cifra
              titulo="Margen"
              valor={margen.porcentaje === null ? '—' : `${margen.porcentaje}%`}
              alerta={(margen.porcentaje ?? 0) < 0}
              detalle={<span className="ayuda">{margen.ordenes} órdenes cerradas</span>}
            />
          </div>

          {/* Un margen global sano puede estar tapando tres órdenes en
              rojo, y son ésas —no el promedio— lo que hay que mirar. */}
          {margen.negativas.length > 0 && (
            <div className="panel">
              <h2>Órdenes que perdieron dinero</h2>
              <table>
                <thead>
                  <tr>
                    <th>Folio</th>
                    <th>Sucursal</th>
                    <th className="numero">Venta</th>
                    <th className="numero">Costo</th>
                    <th className="numero">Pérdida</th>
                  </tr>
                </thead>
                <tbody>
                  {margen.negativas.map((o) => (
                    <tr key={o.folio}>
                      <td className="mono">{folioOT(o.folio)}</td>
                      <td>{o.sucursal ?? '—'}</td>
                      <td className="numero">{moneda(o.venta)}</td>
                      <td className="numero">{moneda(o.costo)}</td>
                      <td className="numero" style={{ color: ROJO, fontWeight: 600 }}>
                        {moneda(o.utilidad)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
}
